// src/pages/Settings/ModuleSettingsForm.jsx
import React, { useEffect, useState } from "react";
import { getSettings, updateSettings } from "../../../services/settingsServices";
import { toast } from "react-toastify";
import Button from "../../../components/Button/Button";

const ROLE_ORDER = [2, 3, 1];
const ROLE_META = {
  2: { short: "U", label: "Utilisateur", chip: "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300" },
  3: { short: "E", label: "Enseignant", chip: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" },
  1: { short: "A", label: "Administrateur", chip: "bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/30 dark:text-fuchsia-300" },
};

// 🔒 Mapping EXACT label ↔ clé display_* (ordre strict demandé)
const ROLE_DEFS = {
  2: [
    { label: "Absences", key: "display_absences" },
    { label: "Calendrier", key: "display_calendrier" },
    { label: "Chat", key: "display_chat" },
    { label: "Boutique", key: "display_boutique" },
    { label: "Commandes & Règlements", key: "display_commandes" },
    { label: "Prépa Physique", key: "display_prepa_physique" },
    { label: "Actualités", key: "display_news" },
    { label: "Galerie Photos", key: "display_galerie" },
    { label: "Lexique", key: "display_lexique" },
    { label: "Arbitrage Judo", key: "display_arbitrage" },
    { label: "Applications Judo", key: "display_applications" },
    { label: "Techniques Judo", key: "display_techniques" },
    { label: "Techniques Quizz", key: "display_techniquesquizz" },
    { label: "Vote Sondage Utilisateur", key: "display_vote" },
  ],
  3: [
    { label: "Appel Présence", key: "display_appels" },
    { label: "Tableau Appel", key: "display_tableauappel" },
    { label: "Passage Ceinture", key: "display_passage" },
    { label: "Grade CN", key: "display_gradecn" },
    { label: "Événements", key: "display_evenements" },
    { label: "Prépa Physique Coaching", key: "display_prepa_physique_coaching" },
    { label: "Suivi Compétitions", key: "display_suivicompetition" },
    { label: "Palmarès", key: "display_palmares" },
    { label: "Objectifs Adhérents", key: "display_objectifs" },
    { label: "ToDoList", key: "display_todolist" },
    { label: "Techniques Enseignants", key: "display_techniquesenseignants" },
    { label: "Catégories", key: "display_categories" },
  ],
  1: [
    { label: "Articles Admin", key: "display_articlesadmin" },
    { label: "Dépenses Recettes", key: "display_depensesrecettes" },
    { label: "Coachings", key: "display_coaching" },
    { label: "Heures Enseignement", key: "display_heures_enseignants" },
    { label: "Inventaire", key: "display_inventaire" },
    { label: "Prêt Matériel", key: "display_pretmateriel" },
    { label: "Chat Page Admin", key: "display_chat_admin" },
    { label: "Message Admin", key: "display_messages_admin" },
    { label: "Vote Sondage Admin", key: "display_voteadmin" },
    { label: "Pages Personnalisées", key: "display_pagespersonnalisees" },
  ],
};

// 📝 Descriptions
const DESCRIPTIONS = {
  // Utilisateurs
  display_absences: "Les adhérents peuvent signaler leurs absences.",
  display_calendrier: "Affichage du calendrier des événements et inscriptions sur ces derniers",
  display_chat: "Module de discussion entre les adhérents du même groupe",
  display_boutique: "Affichage de la boutique en ligne pour les adhérents",
  display_commandes: "Affichage des commandes et règlements des adhérents",
  display_prepa_physique: "Module de préparation physique pour les adhérents",
  display_news: "Affichage des actualités et annonces pour les adhérents",
  display_galerie: "Galerie photos des événements et activités",
  display_lexique: "Lexique des termes et techniques de judo",
  display_arbitrage: "Module d'arbitrage pour les adhérents",
  display_applications: "Applications liées au judo pour les adhérents",
  display_techniques: "Techniques de judo pour les adhérents",
  display_techniquesquizz: "Quiz sur les techniques de judo pour les adhérents",
  display_vote: "Module de vote pour les adhérents",
  // Enseignants
  display_appels: "Gestion des appels de présence des adhérents",
  display_tableauappel: "Tableau de suivi des appels de présence",
  display_passage: "Gestion des passages de ceinture",
  display_gradecn: "Gestion des grades CN",
  display_evenements: "Gestion des événements",
  display_prepa_physique_coaching: "Module de préparation physique pour les enseignants",
  display_suivicompetition: "Suivi des compétitions pour les enseignants",
  display_palmares: "Palmarès des adhérents",
  display_objectifs: "Gestion des objectifs des adhérents",
  display_todolist: "ToDoList des enseignants/administrateurs",
  display_techniquesenseignants: "Gestion des Techniques par les enseignants",
  display_categories: "Catégories d'âges et de poids et répartition des adhérents",
  // Administrateurs
  display_articlesadmin: "Gestion des articles de la boutique + licences-cotisations-réductions",
  display_depensesrecettes: "Gestion des dépenses et recettes",
  display_coaching: "Gestion des heures de coachings",
  display_heures_enseignants: "Gestion des heures de cours des enseignants",
  display_inventaire: "Gestion de l'inventaire",
  display_pretmateriel: "Gestion des prêts de matériel",
  display_chat_admin: "Module de surveillance des discussions dans le chat",
  display_messages_admin: "Module de surveillance des messages.",
  display_voteadmin: "Gestion des votes et sondages.",
  display_pagespersonnalisees: "Gestion des pages personnalisées",
};

const EXPECTED_KEYS = ROLE_ORDER.flatMap((rid) => ROLE_DEFS[rid].map((m) => m.key));
const coerceBool = (val, fallback = false) => {
  if (val === undefined || val === null) return fallback;
  if (typeof val === "boolean") return val;
  if (typeof val === "number") return val !== 0;
  if (typeof val === "string") {
    const v = val.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(v)) return true;
    if (["false", "0", "no", "off"].includes(v)) return false;
  }
  return !!val;
};

export default function ModuleSettingsForm() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await getSettings();
        const first = Array.isArray(data) ? (data[0] || {}) : data || {};
        const raw = first?.dataValues || first;
        setSettings(raw);
      } catch (e) {
        console.error(e);
        toast.error("Impossible de charger les paramètres.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const isEnabled = (key) => coerceBool(settings?.[key], false);

  const toggleKey = (key) =>
    setSettings((prev) => ({
      ...prev,
      [key]: !coerceBool(prev?.[key], false),
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = EXPECTED_KEYS.reduce((acc, k) => {
        acc[k] = coerceBool(settings?.[k], false);
        return acc;
      }, {});
      if (settings?.id != null) payload.id = settings.id;

      await updateSettings(payload);
      toast.success("Paramètres mis à jour.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la mise à jour.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="w-full">
        <div className="mx-auto max-w-7xl px-1 sm:px-1">
          <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
            {/* HEADER (dégradé) */}
            <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8">
              <h2 className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                Modules — Vue par rôle
              </h2>
              <p className="text-white/90 text-sm sm:text-[15px] mt-1">
                Bascule l’affichage des pages par rôle. Ordre et affectation stricts.
              </p>
            </div>

            <div className="p-4 sm:p-6">
              {/* Skeleton simple */}
              <div className="bg-white/70 dark:bg-slate-900/40 rounded-2xl p-6 animate-pulse">
                <div className="h-6 w-48 bg-slate-200 dark:bg-slate-700 rounded mb-5" />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                  {ROLE_ORDER.map((rid) => (
                    <div key={rid} className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4">
                      <div className="h-5 w-32 bg-slate-200 dark:bg-slate-700 rounded mb-3" />
                      {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="h-10 w-full bg-slate-200 dark:bg-slate-700 rounded mb-2" />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full">
      <div className="mx-auto max-w-7xl px-1 sm:px-1">
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
          {/* ===== PARTIE HAUTE CHANGÉE (header dégradé) ===== */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8">
            <h2 className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
              Modules — Vue par rôle
            </h2>
            <p className="text-white/90 text-sm sm:text-[15px] mt-1">
              Bascule l’affichage des pages par rôle. Ordre et affectation stricts.
            </p>
          </div>

          {/* CONTENU EXISTANT CONSERVÉ */}
          <div className="p-4 sm:p-6">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {ROLE_ORDER.map((rid) => (
                  <div
                    key={rid}
                    className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 p-4"
                  >
                    <div className="mb-3 flex items-center justify-between">
                      <h4 className="font-semibold text-slate-900 dark:text-slate-100">{ROLE_META[rid].label}</h4>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${ROLE_META[rid].chip}`}
                      >
                        {ROLE_META[rid].short}
                      </span>
                    </div>

                    <div className="space-y-2">
                      {ROLE_DEFS[rid].map(({ label, key }) => {
                        const enabled = isEnabled(key);
                        return (
                          <div
                            key={`${rid}-${key}`}
                            className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3"
                          >
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                {label}
                              </span>
                              <span className="text-[11px] text-slate-500 dark:text-slate-400">
                                {enabled ? "Affiché" : "Masqué"}
                              </span>
                              <span className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">
                                {DESCRIPTIONS[key] || "contenu"}
                              </span>
                            </div>

                            <button
                              type="button"
                              role="switch"
                              aria-checked={enabled}
                              aria-label={`Basculer ${label}`}
                              onClick={() => toggleKey(key)}
                              className={`relative inline-flex h-6 w-12 shrink-0 cursor-pointer items-center rounded-full transition
                                focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400
                                ${enabled ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"}`}
                            >
                              <span
                                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                                  enabled ? "translate-x-6" : "translate-x-1"
                                }`}
                              />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <Button
                  type="button"
                  className="btn-secondary !rounded-xl"
                  onClick={() => window.history.back()}
                  text="Annuler"
                />
                <Button
                  type="submit"
                  className={`btn-primary !rounded-xl ${saving ? "opacity-60 pointer-events-none" : ""}`}
                  text={saving ? "Enregistrement..." : "Mettre à jour"}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Styles locaux */}
      <style>{`
        html { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
      `}</style>
    </section>
  );
}
